import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";

import "@fontsource-variable/inter";
import "@/shared/assets/main.css";
import { router } from "@/shared/lib/router";
import { initializeMarkerIO } from "@/shared/lib/marker-io";
import { initializeGoogleAnalytics } from "@/shared/lib/google-analytics";
import { resolveSampleRate } from "@/shared/lib/sentry-sample-rate";
import { SENTRY_IGNORED_ERRORS } from "@/shared/lib/sentry-ignored-errors";

import * as Sentry from "@sentry/react";
import { initAnalytics, trackPageviews } from "@/shared/lib/analytics";
import { EntityNotFoundError } from "@/shared/lib/errors";

Sentry.init({
  enabled: true,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  sendDefaultPii: false,
  release: import.meta.env.VITE_SENTRY_RELEASE,
  // Per-PR QA builds only: one PR's errors are `environment:qa qa_pr:N`.
  initialScope: import.meta.env.VITE_QA_PR
    ? { tags: { qa_pr: import.meta.env.VITE_QA_PR } }
    : undefined,
  environment:
    import.meta.env.VITE_SENTRY_ENVIRONMENT ??
    (import.meta.env.DEV ? "local" : "production"),

  // Spotlight — sends event copies to local sidecar (http://localhost:8969)
  spotlight: import.meta.env.DEV,

  integrations: [
    Sentry.browserTracingIntegration(),
    ...(import.meta.env.PROD
      ? [
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ]
      : []),
  ],

  // Performance — full capture in dev, sampled in prod. This rate also governs whether the
  // API traces the requests this page makes; see resolveSampleRate for why.
  tracesSampleRate: resolveSampleRate(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    import.meta.env.DEV ? 1.0 : 0.2,
  ),
  tracePropagationTargets: [new RegExp(`^${import.meta.env.VITE_BACKEND_URL}`)],

  // Session Replay — prod only (integration excluded in dev above)
  ...(import.meta.env.PROD
    ? { replaysSessionSampleRate: 0.1, replaysOnErrorSampleRate: 1.0 }
    : {}),

  // Structured Logs
  enableLogs: true,

  // Filter out non-actionable errors
  ignoreErrors: SENTRY_IGNORED_ERRORS,

  denyUrls: [
    // Chrome extensions
    /extensions\//i,
    /chrome-extension:\/\//i,
    // Third-party scripts
    /productfruits\.com/i,
    /marker\.io/i,
  ],

  // Downgrade expected 404s to warnings (stale links, deleted entities)
  beforeSend: (event, hint) => {
    if (hint.originalException instanceof EntityNotFoundError) {
      event.level = "warning";
    }
    return event;
  },
});

initAnalytics();
trackPageviews(router);

if (import.meta.env.VITE_GA_MEASUREMENT_ID) {
  initializeGoogleAnalytics(import.meta.env.VITE_GA_MEASUREMENT_ID, router);
}

// Register things for typesafety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("app")!;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(<RouterProvider router={router} />);
}

if (import.meta.env.PROD) {
  initializeMarkerIO();
}
